/* eslint-disable react/require-default-props */
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { FlexColumn, FlexRow, Input, Typography } from 'design-system';
import { postData } from '../../../../helpers/fetch_services';
import { resolveLocationId } from '../../../../helpers/resolveLocationId';

function normalizeAnswers(value) {
  if (!value) {
    return [];
  }
  return Array.isArray(value) ? value : [];
}

export default function InventoryTypeAnswer({
  question,
  survey,
  preview = false,
  value,
  onChange,
  interactive = false,
  hasError = false,
}) {
  const intl = useIntl();
  const locationId = resolveLocationId(survey);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const answers = useMemo(() => normalizeAnswers(value), [value]);

  useEffect(() => {
    let cancelled = false;

    const getProductList = async () => {
      if (!question?.productType) {
        setProducts([]);
        return;
      }

      setLoading(true);
      try {
        const result = await postData('inventory/product-list-type-location', {
          location_id: locationId,
          product_type_id: question.productType,
        });
        if (!cancelled && result?.success) {
          setProducts(result?.data || []);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    getProductList();

    return () => {
      cancelled = true;
    };
  }, [locationId, question?.productType, survey]);

  const handleText = (text, productId) => {
    if (!interactive) {
      return;
    }

    const nextValue = text || null;
    const updatedAnswers = answers.filter((ans) => ans.id !== productId);
    onChange?.([...updatedAnswers, { id: productId, value: nextValue }]);
  };

  const getAnswerForProduct = (productId) => answers.find((ans) => ans.id === productId)?.value ?? '';

  if (loading) {
    return (
      <Typography component="p" variant="body2">
        {intl.formatMessage({ id: 'getting-product-list' })}
      </Typography>
    );
  }

  if (!products.length) {
    return null;
  }

  return (
    <FlexColumn align="stretch" fullWidth className={preview ? 'cc-preview-inventory' : 'mt-2'}>
      {products.map((product) => (
        <FlexRow key={product._id} align="center" fullWidth className="cc-preview-inventory__row">
          <Typography component="span" variant="body2" className="cc-preview-inventory__serial">
            {product?.serial_no || ''}
          </Typography>
          <Input
            type="number"
            readOnly={!interactive}
            className="cc-preview-inventory__field"
            placeholder={intl.formatMessage({ id: 'enter-value' })}
            value={getAnswerForProduct(product._id)}
            error={hasError}
            onChange={(event) => handleText(event.target.value, product._id)}
          />
        </FlexRow>
      ))}
    </FlexColumn>
  );
}

InventoryTypeAnswer.propTypes = {
  question: PropTypes.shape({
    productType: PropTypes.string,
  }),
  survey: PropTypes.shape({
    location_id: PropTypes.array,
    clientConnect: PropTypes.shape({
      location_id: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    }),
  }),
  preview: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  onChange: PropTypes.func,
  interactive: PropTypes.bool,
};
